import { useState } from 'react';
import { Check } from 'lucide-react';

import { Popover } from '@/components/atoms/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import * as Primitive from '@/components/ui/select';

interface Option<T extends string> {
  value: T;
  label: string;
}

interface SelectProps<T extends string> {
  name?: string;
  placeholder?: string;
  options: Option<T>[];
  value: T | '';
  onChange: (value: T) => void;
  className?: string;
  invalid?: boolean;
  disabled?: boolean;
}

export function Select<T extends string>({
  name,
  placeholder,
  options,
  value,
  onChange,
  className,
  invalid,
  disabled,
}: SelectProps<T>) {
  return (
    <Primitive.Select value={value} onValueChange={onChange} name={name}>
      <Primitive.SelectTrigger className={className} aria-invalid={invalid} disabled={disabled}>
        <Primitive.SelectValue placeholder={placeholder} />
      </Primitive.SelectTrigger>
      <Primitive.SelectContent>
        <Primitive.SelectGroup>
          {options.map((option) => (
            <Primitive.SelectItem key={option.value} value={option.value}>
              {option.label}
            </Primitive.SelectItem>
          ))}
        </Primitive.SelectGroup>
      </Primitive.SelectContent>
    </Primitive.Select>
  );
}

interface SearchSingleSelectProps<T extends string> {
  trigger: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  selected: T;
  onSelect: (value: T) => void;
  options: Option<T>[];
}

export function SearchSingleSelect<T extends string>({
  trigger,
  align,
  selected,
  onSelect,
  options,
}: SearchSingleSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Popover align={align} trigger={trigger} isOpen={isOpen} onOpenChange={setIsOpen}>
      <SearchSelectContent
        selected={selected}
        onSelect={(value) => {
          setIsOpen(false);
          onSelect(value);
        }}
        options={options}
      />
    </Popover>
  );
}

interface SearchSelectContentProps<T extends string> {
  selected: T;
  onSelect: (value: T) => void;
  options: Option<T>[];
}

export function SearchSelectContent<T extends string>({ selected, onSelect, options }: SearchSelectContentProps<T>) {
  return (
    <Command>
      <CommandInput placeholder='Search ...' />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup>
          {options.map((option) => (
            <CommandItem key={option.value} value={option.label} onSelect={() => onSelect(option.value)}>
              {option.label}
              {selected === option.value && <Check className='ml-auto size-4' />}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
